import { Box, Tab, Tabs as TabsMui, Typography } from "@mui/material";
import React, {
  FunctionComponent,
  ReactElement,
  ReactNode,
  useState,
} from "react";
import { InfoLabel } from "components";

interface TabItem {
  icon?: ReactElement;
  label: ReactNode;
  content: ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  activeTab?: number;
  variant?: "default" | "custom";
  tabsSx?: object;
  displayInfoLabel?: boolean;
  infoLabelTitle?: string;
  infoLabelMessage?: string;
  onChange?: (index: number) => void;
}

export const Tabs: FunctionComponent<TabsProps> = ({
  tabs,
  activeTab = 0,
  variant = "default",
  tabsSx,
  displayInfoLabel = false,
  infoLabelTitle,
  infoLabelMessage,
  onChange,
}) => {
  const [selectedTab, setSelectedTab] = useState(activeTab);
  const isSingleTab = tabs.length === 1;

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    onChange?.(newValue);
  };

  // Conditional Tabs Styles
  const tabsStyles = {
    default: {
      variant: "fullWidth" as const,
    },
    custom: {
      textColor: "inherit" as const,
      TabIndicatorProps: {
        style: {
          backgroundColor: "#054254",
          display: isSingleTab ? "none" : "block",
        },
      },
      sx: {
        ".Mui-selected": {
          color: "#054254 !important",
          fontWeight: "700",
        },
      },
    },
  };

  const singleTabStyle = {
    color: "#054254 !important",
    fontWeight: "700",
    cursor: "default",
    opacity: "1 !important",
  };

  // Conditional Tab Styles
  const tabStyles = {
    default: {
      minHeight: "48px",
    },
    custom: {
      minHeight: "48px",
      margin: "0 0 0 10px",
      textTransform: "none",
      fontWeight: "600",
      fontSize: "14px",
      lineHeight: "18px",
      color: "#054254",
      height: "22px",
      gap: "37px",
      padding: "0",
      marginRight: "36px",
    },
  };

  // Conditonal Tabs container style
  const boxStyles = {
    default: {
      borderBottom: 1,
      borderColor: "divider",
    },
    custom: {
      borderBottom: 1,
      borderColor: "divider",
      display: "flex",
      justifyContent: "space-between",
    },
  };

  // Conditonal Tabs contents styles
  const contentStyles = {
    default: {
      mt: 3,
      flexGrow: 1,
      overflowY: "auto",
      overflowX: "hidden",
    },
    custom: {
      mt: 2,
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <Box sx={boxStyles[variant]}>
        <TabsMui
          value={selectedTab}
          onChange={!isSingleTab ? handleChange : undefined}
          {...tabsStyles[variant]}
          sx={tabsSx}
        >
          {tabs.map((tab) => (
            <Tab
              key={`tab-${tab.label}`}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
              id={`tab-${tab.label}`}
              aria-controls={`tabpanel-${tab.label}`}
              disabled={isSingleTab}
              sx={{
                ...tabStyles[variant],
                ...(isSingleTab && singleTabStyle),
              }}
            />
          ))}
        </TabsMui>
        {displayInfoLabel && (
          <InfoLabel
            labelSx={{ height: "36px", margin: "0px 24px" }}
            title={infoLabelTitle}
            infoMessage={infoLabelMessage || ""}
          />
        )}
      </Box>
      {variant === "default" ? (
        tabs.map((tab, index) => (
          <Box
            sx={contentStyles[variant]}
            key={`tabpanel-${tab.label}`}
            role="tabpanel"
            hidden={selectedTab !== index}
            id={`tabpanel-${tab.label}`}
            aria-labelledby={`tab-${tab.label}`}
          >
            {selectedTab === index && tab.content}
          </Box>
        ))
      ) : (
        <Box sx={contentStyles[variant]}>
          {tabs[selectedTab]?.content || (
            <Typography variant="body2">No content available</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
